"use client";

import { EmailComposerKeyboardAnalytics, trackEmailComposerAction } from "../../analytics";
import { prepareEmailHtml } from "../../blocks/html-fallback/prepare-html";
import { sanitizeEmailDocument } from "../../blocks/html-fallback/sanitize-document";
import { createEmailExtensions } from "../../blocks/registry";
import { normalizeEmailContent } from "../../blocks/row/normalize-content";
import type { EmailComposerMode } from "../../context";
import { createEmailMention } from "../email-mention";
import { insertImageUploads } from "../image-upload";
import { createNodeSelectionDragPlugin } from "../node-selection-drag-plugin";
import { Extension } from "@tiptap/core";
import { Node } from "@tiptap/pm/model";
import { Extensions, useEditor } from "@tiptap/react";
import { JSONContent } from "@tiptap/react";
import { type RefObject, useRef } from "react";
import type { SuggestionItem } from "@/types/suggestions";
interface UseEmailEditorOptions {
  mode?: EmailComposerMode;
  initialContent?: JSONContent;
  onChange?: (content: JSONContent) => void;
  variables?: SuggestionItem[];
  onImageUpload?: ((file: File) => Promise<string>) | null;
  onAddFiles?: ((files: File[]) => {
    added: number;
    errors: string[];
  }) | null;
  placeholder?: string;
  /** When true, inserting a suggestion via @ inserts the resolved content as editable text instead of a mention placeholder. */
  resolveOnInsert?: boolean;
  /** Additional TipTap extensions to register (e.g. slash commands). */
  additionalExtensions?: Extensions;
  /** Initial marketing state — used to show the unsubscribe footer decoration from the start. */
  initialIsMarketing?: boolean;
}

/**
 * Resolve a raw document position to a block-level insertion point.
 * posAtCoords can land inside a paragraph which doesn't accept block nodes,
 * so we walk up to find the nearest emailBlock or list boundary.
 */
function resolveBlockInsertPos(doc: Node, pos: number): number {
  const $pos = doc.resolve(pos);
  for (let d = $pos.depth; d > 0; d--) {
    const node = $pos.node(d);
    const group = node.type.spec.group as string | undefined;
    if (group?.includes("emailBlock") || node.type.name === "bulletList" || node.type.name === "orderedList") {
      return $pos.after(d);
    }
  }
  return $pos.pos;
}
declare module "@tiptap/core" {
  interface Storage {
    emailImageUpload: {
      /** The composer's image upload, or null where images are turned off. */
      upload: () => ((file: File) => Promise<string>) | null;
    };
  }
}

/**
 * Exposes the composer's upload function to extensions that only see the
 * editor — the `/` menu's background-image section uploads through it.
 */
function createImageUploadStorage(uploadRef: RefObject<((file: File) => Promise<string>) | null | undefined>) {
  return Extension.create({
    name: "emailImageUpload",
    addStorage() {
      return {
        upload: () => uploadRef.current ?? null
      };
    }
  });
}
function partitionFiles(dataTransfer: DataTransfer) {
  const images: File[] = [];
  const other: File[] = [];
  for (const file of Array.from(dataTransfer.files)) {
    if (file.type.startsWith("image/")) {
      images.push(file);
    } else {
      other.push(file);
    }
  }
  return {
    images,
    other
  };
}
export function useEmailEditor({
  mode = "simple",
  initialContent,
  onChange,
  variables = [],
  onImageUpload,
  onAddFiles,
  placeholder,
  resolveOnInsert,
  additionalExtensions,
  initialIsMarketing
}: UseEmailEditorOptions = {}) {
  const variablesRef = useRef(variables);
  variablesRef.current = variables;
  const onImageUploadRef = useRef(onImageUpload);
  onImageUploadRef.current = onImageUpload;
  const onAddFilesRef = useRef(onAddFiles);
  onAddFilesRef.current = onAddFiles;
  const editor_0 = useEditor({
    extensions: [...createEmailExtensions({
      placeholder,
      initialIsMarketing,
      draggable: mode === "block",
      mode
    }), createEmailMention(variablesRef, {
      resolveOnInsert
    }), EmailComposerKeyboardAnalytics, createImageUploadStorage(onImageUploadRef), ...(mode === "block" ? [Extension.create({
      name: "nodeSelectionDrag",
      addProseMirrorPlugins() {
        return [createNodeSelectionDragPlugin()];
      }
    })] : []), ...(additionalExtensions ?? [])],
    content: normalizeEmailContent(sanitizeEmailDocument(initialContent)),
    immediatelyRender: false,
    onUpdate: ({
      editor
    }) => {
      const json = editor.getJSON();
      onChange?.(json);
    },
    editorProps: {
      // Pasted markup comes through the same door a loaded template does: the
      // scaffold unwrapped, anything that can execute stripped, and comments
      // made visible to a parser that would otherwise never see them.
      transformPastedHTML: html => prepareEmailHtml(html),
      handlePaste(view, event) {
        if (!event.clipboardData) return false;
        const {
          images,
          other
        } = partitionFiles(event.clipboardData);

        // Attach non-image files
        if (other.length > 0 && onAddFilesRef.current) {
          onAddFilesRef.current(other);
          trackEmailComposerAction("file_attached", "paste", {
            num_files: other.length
          });
        }

        // Insert images inline with correctly-sized skeleton placeholders
        const upload = onImageUploadRef.current;
        if (images.length > 0 && upload) {
          void insertImageUploads(view, images, upload, placeholder_0 => view.dispatch(view.state.tr.replaceSelectionWith(placeholder_0)), "paste");
        }

        // Handle the event if we processed any files
        return images.length > 0 || other.length > 0;
      },
      handleDrop(view_0, event_0) {
        if (!event_0.dataTransfer) return false;

        // Internal ProseMirror drags (e.g. DragHandle block reorder) include
        // content types beyond just "Files". Let ProseMirror handle those.
        const types = event_0.dataTransfer.types;
        const isInternalDrag = types.includes("text/html") || types.includes("text/plain") || types.some(t => t.startsWith("application/"));
        if (isInternalDrag) return false;
        const {
          images: images_0,
          other: other_0
        } = partitionFiles(event_0.dataTransfer);
        if (images_0.length === 0 && other_0.length === 0) return false;
        event_0.preventDefault();

        // Attach non-image files
        if (other_0.length > 0 && onAddFilesRef.current) {
          onAddFilesRef.current(other_0);
          trackEmailComposerAction("file_attached", "drag_drop", {
            num_files: other_0.length
          });
        }

        // Insert images inline with correctly-sized skeleton placeholders
        const upload_0 = onImageUploadRef.current;
        if (images_0.length > 0 && upload_0) {
          const coordinates = view_0.posAtCoords({
            left: event_0.clientX,
            top: event_0.clientY
          });
          const pos = coordinates?.pos ?? view_0.state.selection.anchor;

          // Each image goes after the last one placed, not back at the
          // drop point, or a multi-file drop lands in reverse.
          let at = resolveBlockInsertPos(view_0.state.doc, pos);
          void insertImageUploads(view_0, images_0, upload_0, placeholder_1 => {
            view_0.dispatch(view_0.state.tr.insert(at, placeholder_1));
            at += placeholder_1.nodeSize;
          }, "drag_drop");
        }
        return true;
      }
    }
  });
  return editor_0;
}